import { v7 as uuidv7 } from 'uuid';
import { jwtDecode } from 'jwt-decode';
import { ACTIVITY_LOGGER_ENABLED, EVENT_NAMES, EVENT_TYPES, UTM_PARAMS } from './constants';
import {
  addPendingSignUp,
  getPendingSignUps,
  isStorageAvailable,
  removePendingSignUps,
} from './storage';
import { UserEvent } from './types';

export type UtmParams = Partial<Record<(typeof UTM_PARAMS)[number], string>>;

// The UTM parameters present in a URL's query string; missing or empty ones are left out
export const getUtmParams = (search: string): UtmParams => {
  const params = new URLSearchParams(search);

  return UTM_PARAMS.reduce<UtmParams>((utm, key) => {
    const value = params.get(key)?.trim();
    return value ? { ...utm, [key]: value } : utm;
  }, {});
};

// Called when an account is created on the sign-up page. The user has no token yet, so the event
// is kept apart from the queue with the time it was captured, and sent once the user has logged in
// with the same email and been loaded (see sendPendingSignUps).
export const recordSignUp = ({
  email,
  hq,
  referrer,
  utm,
}: {
  email: string;
  hq: string | null;
  referrer: string | null;
  utm: UtmParams;
}): void => {
  if (!ACTIVITY_LOGGER_ENABLED || !email || !isStorageAvailable()) return;

  addPendingSignUp({
    email: email.trim().toLowerCase(),
    event: {
      eventId: uuidv7(),
      eventType: EVENT_TYPES.USER_ACCOUNT,
      eventName: EVENT_NAMES.SIGN_UP,
      occurredAt: new Date().toISOString(),
      eventDetails: { hq: hq || null, referrer: referrer || null, utm },
    },
  });
};

const getTokenEmail = (accessToken: string): string | null => {
  try {
    const claims = jwtDecode<{ email?: string; preferred_username?: string }>(accessToken);
    return (claims.email ?? claims.preferred_username)?.trim().toLowerCase() || null;
  } catch {
    return null;
  }
};

// Called when tracking starts for a logged in user. Takes the sign-ups recorded on this browser for
// the email the user logged in with, so they can join the queue and go out with the next batch.
// Sign-ups for other emails stay for a later visit.
export const takePendingSignUps = (accessToken: string | null): UserEvent[] => {
  if (!ACTIVITY_LOGGER_ENABLED || !accessToken) return [];

  const email = getTokenEmail(accessToken);
  if (!email) return [];

  const signUps = getPendingSignUps().filter((signUp) => signUp.email === email);
  removePendingSignUps(signUps.map((signUp) => signUp.event.eventId));

  return signUps.map((signUp) => signUp.event);
};
